import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Work Locations library (read-only foundation).
// Strictly additive, locations are referenced by other features later
// (preferences, rota, rules) but nothing here enforces or automates anything.

const EMPTY = {
  name: "", code: "", type: "site", address: "",
  timezone: "", colour: "#1D4ED8", notes: "",
};

const TONE_PRESET = [
  "#1D4ED8", "#0EA5E9", "#0891B2", "#10B981", "#84CC16",
  "#F59E0B", "#F97316", "#EF4444", "#EC4899", "#A855F7", "#64748B",
];

export default function LocationsPage() {
  const { user } = useAuth();
  const canWrite = user?.role === "company_admin" || user?.role === "super_admin";

  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [query, setQuery] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const qs = new URLSearchParams();
    if (includeArchived) qs.set("include_archived", "true");
    if (typeFilter) qs.set("type", typeFilter);
    if (query.trim()) qs.set("q", query.trim());
    const [{ data: ls }, { data: ts }] = await Promise.all([
      api.get(`/locations?${qs.toString()}`),
      api.get("/locations/_meta/types"),
    ]);
    setRows(ls);
    setTypes(ts);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [includeArchived, typeFilter]);

  const typeLabel = useMemo(() => {
    const m = {};
    types.forEach((t) => { m[t.id] = t.label; });
    return (id) => m[id] || id;
  }, [types]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      code: row.code || "",
      type: row.type || "site",
      address: row.address || "",
      timezone: row.timezone || "",
      colour: row.colour || "#1D4ED8",
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        type: form.type || "site",
        address: form.address.trim() || null,
        timezone: form.timezone.trim() || null,
        colour: form.colour || null,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await api.patch(`/locations/${editing.location_id}`, body);
        toast.success("Location updated");
      } else {
        await api.post("/locations", body);
        toast.success("Location created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (row) => {
    if (!window.confirm(`Archive "${row.name}"? It will be hidden but no existing data is affected.`)) return;
    try {
      await api.post(`/locations/${row.location_id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };

  const restore = async (row) => {
    try {
      await api.post(`/locations/${row.location_id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };

  const filteredRows = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.code || "").toLowerCase().includes(q) ||
      (r.address || "").toLowerCase().includes(q));
  }, [rows, query]);

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0B1A36]">
            Locations
          </h1>
          <p className="text-sm text-[#4B5773]">
            Add the places your team works from, offices, depots, client sites, or remote setups.
            Locations appear on roster cards, filter reports, drive site-specific staffing
            requirements, and shape leave decisions.
          </p>
        </div>
        {canWrite && (
          <Button onClick={openCreate} data-testid="locations-create-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
            <Plus className="h-4 w-4 mr-1" />Add location
          </Button>
        )}
      </div>

      <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
        data-testid="locations-helper-card">
        <div className="text-sm font-semibold text-[#0B1A36] mb-2">What can I do with locations?</div>
        <ul className="text-xs text-[#1F2A44] space-y-1 list-disc pl-5">
          <li>Schedule shifts by site</li>
          <li>Set site-specific staffing requirements</li>
          <li>Filter reports by location</li>
          <li>Record employees&apos; preferred and restricted locations</li>
          <li>Use them in future automation to suggest the best fit</li>
        </ul>
      </div>

      <Card>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Search name, code or address"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-64"
              data-testid="locations-search"
            />
            <Select value={typeFilter || "all"}
                    onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-40" data-testid="locations-type-filter">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="text-xs text-[#4B5773] inline-flex items-center gap-1.5">
              <input type="checkbox" checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                data-testid="locations-include-archived" />
              Include archived
            </label>
          </div>
          <div className="text-xs text-[#4B5773]"
                data-testid="locations-count">
            {filteredRows.length} location{filteredRows.length === 1 ? "" : "s"}
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="text-center py-10 text-[#4B5773]"
                data-testid="locations-empty">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No locations yet.</p>
            {canWrite && (
              <p className="text-xs mt-1">
                Click <span className="font-medium">Add location</span> to create your first one.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#4B5773] border-b border-slate-200">
                  <th className="py-2 pr-2">Name</th>
                  <th className="py-2 pr-2">Code</th>
                  <th className="py-2 pr-2">Type</th>
                  <th className="py-2 pr-2">Address</th>
                  <th className="py-2 pr-2">Timezone</th>
                  <th className="py-2 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.location_id}
                      data-testid={`location-row-${r.location_id}`}
                      className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: r.colour || "#64748B" }}
                              data-testid={`location-colour-${r.location_id}`} />
                        <span className={r.is_archived ? "text-[#4B5773] italic" : "text-[#0B1A36] font-medium"}>
                          {r.name}
                        </span>
                        {r.is_archived && (
                          <span className="text-[10px] uppercase tracking-widest text-[#4B5773] px-1.5 py-0.5 rounded bg-slate-100">
                            Archived
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-[#4B5773]">{r.code || "-"}</td>
                    <td className="py-2 pr-2 text-[#4B5773]">{typeLabel(r.type)}</td>
                    <td className="py-2 pr-2 text-[#4B5773] truncate max-w-[260px]">{r.address || "-"}</td>
                    <td className="py-2 pr-2 text-[#4B5773]">{r.timezone || "-"}</td>
                    <td className="py-2 pr-2 text-right">
                      {canWrite && (
                        <div className="inline-flex items-center gap-1">
                          <Button variant="ghost" size="sm"
                            onClick={() => openEdit(r)}
                            data-testid={`location-edit-${r.location_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {r.is_archived ? (
                            <Button variant="ghost" size="sm"
                              onClick={() => restore(r)}
                              data-testid={`location-restore-${r.location_id}`}>
                              <ArchiveRestore className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm"
                              onClick={() => archive(r)}
                              data-testid={`location-archive-${r.location_id}`}>
                              <Archive className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="location-editor">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit location" : "Add location"}</DialogTitle>
            <DialogDescription>
              Add the places your team works from, offices, depots, remote
              setups, or client sites, so you can plan and track shifts by location.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="loc-name">Name</Label>
              <Input id="loc-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. London Office"
                data-testid="location-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="loc-code">Code (optional)</Label>
                <Input id="loc-code" value={form.code}
                  maxLength={8}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="LON"
                  data-testid="location-code" />
                <p className="text-[11px] text-[#4B5773] mt-1">
                  A short code like LON or MWN, shown on narrow roster cells when the full name doesn&apos;t fit.
                </p>
              </div>
              <div>
                <Label htmlFor="loc-type">Type</Label>
                <Select value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger id="loc-type" data-testid="location-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#4B5773] mt-1">
                  Used to group locations in reports, for example, all &lsquo;Depot&rsquo; sites together.
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="loc-address">Address (optional)</Label>
              <Input id="loc-address" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="10 Downing St, London SW1A 2AA"
                data-testid="location-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="loc-tz">Timezone (optional)</Label>
                <Input id="loc-tz" value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  placeholder="Europe/London"
                  data-testid="location-timezone" />
              </div>
              <div>
                <Label>Chip colour</Label>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {TONE_PRESET.map((c) => (
                    <button key={c} type="button"
                      onClick={() => setForm({ ...form, colour: c })}
                      data-testid={`location-colour-pick-${c}`}
                      className={`w-5 h-5 rounded-full border ${form.colour === c ? "ring-2 ring-offset-1 ring-[#0B1A36]" : "border-slate-300"}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Colour ${c}`} />
                  ))}
                </div>
                <p className="text-[11px] text-[#4B5773] mt-1">
                  The dot shown next to this location on roster cards.
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="loc-notes">Notes (optional)</Label>
              <Textarea id="loc-notes" value={form.notes} rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Reception desk, parking instructions, anything useful for the team"
                data-testid="location-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
              data-testid="location-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy}
              data-testid="location-save"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              {editing ? "Save changes" : "Create location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
